import {
  BiBookmark,
  BiCheckDouble,
  BiMessageRounded,
  BiShareAlt,
} from 'react-icons/bi';
import { BsBookmarkFill } from 'react-icons/bs';
import { ActionIcon, Group, Tooltip } from '@mantine/core';
import {
  CHAT_ENABLED_STATUSES,
  JobMutationAction,
  JobMutationState,
  TransformedJobItem,
} from '@worker/types/myJobs.type';
import { isMobile } from 'react-device-detect';

export interface JobActionsToolbarProps {
  recommendation: TransformedJobItem;
  mutation: JobMutationState;
  onDismiss?: () => void;
  onBookmark?: () => void;
  onShare?: () => void;
  onMessage?: () => void;
  isPastJob?: boolean;
  /** Render icons inline without the Group wrapper. */
  spread?: boolean;
}

export default function JobActionsToolbar({
  recommendation,
  mutation,
  onDismiss,
  onBookmark,
  onShare,
  onMessage,
  isPastJob = false,
  spread = false,
}: JobActionsToolbarProps) {
  const { job, jobAssignmentId, jobAssignmentStatus } = recommendation;
  const isSaved = !!recommendation.savedAt;

  const isThisJobDismissing =
    mutation.action === JobMutationAction.DISMISS &&
    mutation.targetId === recommendation.id;
  const isThisJobSaving =
    (mutation.action === JobMutationAction.SAVE ||
      mutation.action === JobMutationAction.UNSAVE) &&
    mutation.targetId === job?.id;

  const isChatEnabled =
    !!jobAssignmentId &&
    !!jobAssignmentStatus &&
    CHAT_ENABLED_STATUSES.includes(jobAssignmentStatus);

  const chatButton = onMessage ? (
    <ActionIcon
      variant="transparent"
      onClick={onMessage}
      disabled={!isChatEnabled}
      aria-label="Message"
    >
      <BiMessageRounded
        size={24}
        color={isChatEnabled ? 'var(--black)' : 'var(--mantine-color-gray-5)'}
      />
    </ActionIcon>
  ) : null;

  const icons = (
    <>
      {onDismiss && (
        <Tooltip label="Dismiss this job">
          <ActionIcon
            variant="transparent"
            color="var(--black)"
            onClick={onDismiss}
            loading={isThisJobDismissing}
            aria-label="Dismiss job"
          >
            <BiCheckDouble size={24} />
          </ActionIcon>
        </Tooltip>
      )}
      {onBookmark && (
        <Tooltip label={isSaved ? 'Remove from saved' : 'Save job'}>
          <ActionIcon
            variant="transparent"
            onClick={onBookmark}
            loading={isThisJobSaving}
            aria-label={isSaved ? 'Unsave job' : 'Save job'}
          >
            {isSaved ? (
              <BsBookmarkFill size={24} color="var(--yellow)" />
            ) : (
              <BiBookmark size={24} color="var(--black)" />
            )}
          </ActionIcon>
        </Tooltip>
      )}
      {onShare && (
        <Tooltip label={isPastJob ? "Can't share past jobs" : 'Share job'}>
          <ActionIcon
            variant="transparent"
            onClick={onShare}
            disabled={isPastJob}
            aria-label="Share job"
          >
            <BiShareAlt
              size={24}
              color={isPastJob ? 'var(--mantine-color-gray-5)' : 'var(--black)'}
            />
          </ActionIcon>
        </Tooltip>
      )}
      {chatButton &&
        (isChatEnabled ? (
          chatButton
        ) : (
          <Tooltip
            label="Apply first, then you can message the company"
            events={{ hover: true, focus: true, touch: true }}
          >
            {chatButton}
          </Tooltip>
        ))}
    </>
  );

  if (spread) return icons;
  return (
    <Group gap={4} justify={isMobile ? 'space-around' : 'start'}>
      {icons}
    </Group>
  );
}
